/* =======================================================================
   GalleryPage — Pagina Galleria fotografica
   =======================================================================
   Le foto sono dichiarate in src/data/gallery.js.
   Layout "masonry" (a colonne) che preserva l'aspect ratio originale —
   le foto non vengono mai tagliate. Click sulla foto → lightbox a tutta pagina
   con frecce ← → e tasto Esc.
   ======================================================================= */
import { useLang } from "@/context/LanguageContext";
import { useEffect, useState, useCallback } from "react";
import copy from "@/data/copy";
import { galleryImages } from "@/data/gallery";

export default function GalleryPage() {
  const { tr } = useLang();
  const g = copy.pages.gallery;
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const list = galleryImages;

  const openAt = useCallback((idx) => setLightboxIdx(idx), []);
  const close = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + list.length) % list.length)),
    [list.length]
  );
  const next = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % list.length)),
    [list.length]
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIdx, close, prev, next]);

  return (
    <div data-testid="page-gallery" className="pt-24 lg:pt-32 bg-[#F7F4EB] text-[#2C2C2A]">
      {/* Heading */}
      <section className="pt-12 pb-14 lg:pt-20 lg:pb-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#737554] mb-6">
            — {tr(g.eyebrow)}
          </p>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <h1 className="lg:col-span-8 font-serif font-light text-[clamp(2.4rem,5vw,4.6rem)] leading-[1.02] tracking-tight whitespace-pre-line">
              {tr(g.title)}
            </h1>
            <p className="lg:col-span-4 text-base lg:text-lg font-light leading-relaxed text-[#3a3a37]">
              {tr(g.sub)}
            </p>
          </div>
          <p className="mt-8 text-[0.72rem] tracking-[0.22em] uppercase text-[#737554]">
            {list.length} foto
          </p>
        </div>
      </section>

      {/* Masonry grid using CSS columns - preserves aspect ratio */}
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 lg:gap-4 [column-fill:_balance]">
            {list.map((img, i) => (
              <button
                key={i}
                data-testid={`gallery-item-${i}`}
                onClick={() => openAt(i)}
                className="group relative w-full mb-3 lg:mb-4 break-inside-avoid overflow-hidden rounded-md bg-[#EAE3D2] block"
                aria-label={tr(img.alt)}
              >
                <img
                  src={img.src}
                  alt={tr(img.alt)}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2A]/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-[#1c1a17]/96 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            className="absolute top-5 right-5 h-12 w-12 grid place-items-center text-[#F7F4EB] border border-[#F7F4EB]/30 hover:border-[#F7F4EB] rounded-full transition-colors"
            aria-label="Chiudi"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 lg:left-8 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center text-[#F7F4EB] border border-[#F7F4EB]/30 hover:border-[#F7F4EB] rounded-full transition-colors"
            aria-label="Precedente"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 lg:right-8 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center text-[#F7F4EB] border border-[#F7F4EB]/30 hover:border-[#F7F4EB] rounded-full transition-colors"
            aria-label="Successiva"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <figure
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[88vh] flex flex-col items-center gap-4"
          >
            <img
              data-testid="lightbox-image"
              src={list[lightboxIdx].src}
              alt={tr(list[lightboxIdx].alt)}
              className="max-w-full max-h-[80vh] object-contain rounded-md shadow-2xl"
            />
            <figcaption className="text-center font-serif italic text-[#F7F4EB]/85">
              <span className="text-[0.7rem] tracking-[0.22em] uppercase text-[#E6C9A8]">
                {lightboxIdx + 1} / {list.length}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
